import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { StepEditModal } from '../../components/klanttraject/StepEditModal';
import { MailPreviewModal } from '../../components/klanttraject/MailPreviewModal';
import { Plus, Eye, Pencil, ArrowLeft, Loader2, HelpCircle } from 'lucide-react';
import { StepHelpModal } from '../../components/klanttraject/StepHelpModal';
import type { JourneyStep } from '../../types/klanttraject';

export function AdminKlanttrajectBeheer() {
  const navigate = useNavigate();
  const [steps, setSteps] = useState<JourneyStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<JourneyStep | null | 'new'>(null);
  const [previewing, setPreviewing] = useState<string | null>(null);
  const [helping, setHelping] = useState<JourneyStep | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await supabase.from('journey_steps').select('*').order('step_order', { ascending: true });
      if (!cancelled) {
        setSteps((data ?? []) as JourneyStep[]);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reload]);

  async function toggleActive(s: JourneyStep) {
    await supabase.from('journey_steps').update({ is_active: !s.is_active }).eq('id', s.id);
    setReload(r => r + 1);
  }

  return (
    <AdminLayout>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/admin/klanttraject')} className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Terug naar overzicht
          </button>
          <button onClick={() => setEditing('new')} className="px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded flex items-center gap-1">
            <Plus className="w-4 h-4" /> Nieuwe stap
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-600">
                <tr>
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2">step_key</th>
                  <th className="px-3 py-2">title</th>
                  <th className="px-3 py-2">type</th>
                  <th className="px-3 py-2">delay</th>
                  <th className="px-3 py-2">template</th>
                  <th className="px-3 py-2">actief</th>
                  <th className="px-3 py-2 text-right">acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {steps.map(s => (
                  <tr key={s.id} className={s.is_active ? '' : 'opacity-50'}>
                    <td className="px-3 py-2">{s.step_order}</td>
                    <td className="px-3 py-2 font-mono text-xs">{s.step_key}</td>
                    <td className="px-3 py-2">{s.title}</td>
                    <td className="px-3 py-2"><span className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{s.type}</span></td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {s.type === 'event_celebration'
                        ? <span className="italic text-slate-500">bij gebeurtenis</span>
                        : <>{s.delay_amount} {s.delay_unit}{s.fixed_send_time ? ` @ ${s.fixed_send_time}` : ''}</>}
                    </td>
                    <td className="px-3 py-2 text-xs font-mono">{s.email_template ?? '—'}</td>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={s.is_active} onChange={() => toggleActive(s)} />
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button title="Uitleg" onClick={() => setHelping(s)} className="text-slate-600 hover:text-teal-600 p-1 inline-block"><HelpCircle className="w-4 h-4" /></button>
                      {s.email_template && (
                        <button title="Mail-preview" onClick={() => setPreviewing(s.email_template!)} className="text-slate-600 hover:text-slate-900 p-1 inline-block ml-1"><Eye className="w-4 h-4" /></button>
                      )}
                      <button title="Bewerken" onClick={() => setEditing(s)} className="text-slate-600 hover:text-slate-900 p-1 inline-block ml-1"><Pencil className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {editing !== null && (
          <StepEditModal
            step={editing === 'new' ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={() => setReload(r => r + 1)}
          />
        )}
        {previewing && <MailPreviewModal templateType={previewing} onClose={() => setPreviewing(null)} />}
        {helping && <StepHelpModal stepKey={helping.step_key} title={helping.title} description={helping.description} onClose={() => setHelping(null)} />}
      </div>
    </AdminLayout>
  );
}
